import { useEffect, useState } from 'react';
import { ArrowLeft, ArrowRight, Bike, Clock, Copy, MapPin } from 'lucide-react';
import { NumberField } from '../ui/NumberField';
import { formatPrecio } from '@renderer/utils/formatters';
import { pesosACentavos, centavosAPesos } from '@shared/centavos';
import { mensajeCotizacionAlGrupo } from '@shared/mensajesWhatsapp';
// Sprint 77 — Los atajos de tarifa: zonas configuradas + lo aprendido de este rumbo.
// Sprint 111 — Y el tercero: la tarifa que le toca por distancia desde el negocio.
import { textoDistancia } from '@shared/distanciaEnvio';
import { explicarTarifa } from '@shared/zonaEnvio';
import type { SugerenciaEnvioVista } from '@shared/types';
import { copiarAlPortapapeles } from '@renderer/lib/portapapeles';
import type { EntregaInfo } from './DondeEntregarForm';
import { Modal } from '@renderer/components/ui/Modal';
import { etiquetaPaso } from './pasosDomicilio';

/**
 * Sprint 66 — Paso 4 del flujo de domicilio: **cuánto cuesta llevarlo**.
 *
 * Era la segunda mitad de `DireccionEnvioForm`, que pedía dirección y costo juntos después del
 * menú. La dirección se adelantó al paso 2 —donde el oficio dice que va, para poder decir "hasta
 * allá no llegamos" antes de capturar la comida— y aquí quedó sólo el dinero, que sí depende del
 * pedido ya armado.
 *
 * Sprint 76 — Este paso pasó a ser **la cotización completa**, no sólo el monto:
 *
 *  - Se copia el mensaje para el grupo MOTO MANDADO con un botón (sin datos del cliente: ese
 *    grupo es un chat con gente que el negocio no controla).
 *  - Del grupo vuelven **dos** datos y ahora se capturan los dos: cuánto cobran y **quién viene
 *    por él**. El segundo se tecleaba en Despacho, media hora después, con la moto esperando en
 *    la puerta.
 *  - Se puede **dejar el pedido esperando** la respuesta del grupo en lugar de bloquear la
 *    pantalla: mientras tanto se atiende a otro cliente.
 */
export interface CostoEnvioInfo {
  costoEnvio: number;
  envioPagaCliente: boolean;
  /** Sprint 76 — Quién recoge, según contestó el grupo. */
  nombreRepartidor?: string;
  /** Sprint 77 — El envío corre por cuenta del negocio. */
  envioCortesia?: boolean;
  /**
   * Sprint 137 — El pedido sale sin cobrar y se cobra al entregarlo.
   *
   * Se decide en este paso y no en uno nuevo porque aquí ya se sabe el total y es la
   * conversación que quien atiende está teniendo con el cliente en ese mismo momento. Después
   * de este paso el pedido se va a la parrilla.
   */
  pagaAlEntregar?: boolean;
  /** Con cuánto dijo que va a pagar, en centavos. Para que la moto salga con el cambio. */
  pagaCon?: number;
}

/** Lo que se manda al backend: la entrega del paso 2 más el dinero de este paso. */
export type EnvioInfo = EntregaInfo & CostoEnvioInfo;

interface Props {
  onSiguiente: (costo: CostoEnvioInfo) => void;
  onAtras: () => void;
  /**
   * Sprint 76 — Guardar el pedido tal como está y volver al tablero, sin cotizar.
   *
   * Es la salida que faltaba: hasta ahora, la única forma de atender a otro cliente mientras el
   * grupo contestaba era cancelar el pedido o dejar la pantalla abierta.
   */
  onDejarEsperando?: (parcial: CostoEnvioInfo) => void;
  inicial?: CostoEnvioInfo | null;
  /** A dónde va, para no tener que regresar a verlo. */
  entrega?: EntregaInfo | null;
  totalPedido: number;
  monedaSimbolo: string;
  /** Folio provisional para el mensaje del grupo. */
  folio?: number | null;
  nombreNegocio?: string | null;
  enviando?: boolean;
  /**
   * Sprint 137 — Para poder decir si este cliente ya ha pedido antes.
   *
   * Es un **dato**, no un candado: no bloquea el contra entrega para un cliente nuevo. Si el
   * negocio hoy sólo se lo da a los conocidos, tiene la información enfrente; si mañana se lo
   * ofrece a cualquiera, no hay nada que reconfigurar ni a quién llamar.
   */
  telefonoCliente?: string | null;
}

export function CostoEnvioForm({
  onSiguiente,
  onAtras,
  onDejarEsperando,
  inicial,
  entrega,
  totalPedido,
  monedaSimbolo,
  folio,
  nombreNegocio,
  enviando = false,
  telefonoCliente,
}: Props) {
  const [costoEnvio, setCostoEnvio] = useState(inicial?.costoEnvio ?? 0);
  /**
   * Sprint 98 — El default pasa a ser "incluirlo en el monto a pagar".
   *
   * Venía en `true` (contra-entrega) de cuando el domicilio se cobraba en la puerta. Hoy el negocio
   * cobra por adelantado por transferencia, así que lo normal es que el envío vaya dentro del total
   * — y por eso es la primera opción de la lista. Un default que hay que cambiar en casi todos los
   * pedidos es un default equivocado.
   *
   * Cambia el **total a cobrar** por omisión: con un envío de $50, el mensaje al cliente pedirá
   * $50 más que antes, salvo que se elija contra-entrega a mano.
   */
  const [envioPagaCliente, setEnvioPagaCliente] = useState(inicial?.envioPagaCliente ?? false);
  const [nombreRepartidor, setNombreRepartidor] = useState(inicial?.nombreRepartidor ?? '');
  const [envioCortesia, setEnvioCortesia] = useState(inicial?.envioCortesia ?? false);
  /**
   * Sprint 137 — ¿Se cobra al entregar?
   *
   * Nace en `false`: lo normal sigue siendo que el cliente transfiera antes de que la comida
   * toque la parrilla. Un default que manda comida a crédito sin que nadie lo haya decidido
   * sería exactamente el defecto que este campo existe para evitar.
   */
  const [pagaAlEntregar, setPagaAlEntregar] = useState(inicial?.pagaAlEntregar ?? false);
  /** Texto libre: se convierte a centavos al enviar. Vacío = no dijo con cuánto paga. */
  const [pagaCon, setPagaCon] = useState(
    inicial?.pagaCon != null ? String(centavosAPesos(inicial.pagaCon)) : '',
  );
  const [error, setError] = useState<string | null>(null);
  const [copiado, setCopiado] = useState(false);
  const [sugerencia, setSugerencia] = useState<SugerenciaEnvioVista | null>(null);

  /**
   * Sprint 77 — Qué se ha cobrado por llevar a este rumbo.
   *
   * Se consulta al abrir el paso, no al teclear: llega antes de que el operador necesite el
   * número, y si no llega no estorba — el campo funciona igual sin ella.
   */
  useEffect(() => {
    (async () => {
      const res = await window.api.pedidos.sugerenciaEnvio({
        latitud: entrega?.latitud,
        longitud: entrega?.longitud,
      });
      if (res.ok) setSugerencia(res.data);
    })();
  }, [entrega?.latitud, entrega?.longitud]);

  /**
   * Sprint 137 — Cuántas veces ha pedido antes este cliente.
   *
   * `null` = todavía no se sabe (o no hay teléfono con qué buscar), y entonces no se afirma
   * nada. Decir "es cliente nuevo" porque una consulta no ha vuelto sería inventarse un hecho
   * sobre alguien que quizá lleva dos años pidiendo.
   */
  const [pedidosPrevios, setPedidosPrevios] = useState<number | null>(null);
  useEffect(() => {
    const tel = telefonoCliente?.trim();
    if (!tel) {
      setPedidosPrevios(null);
      return;
    }
    (async () => {
      const res = await window.api.clientes.porTelefono(tel);
      if (res.ok) setPedidosPrevios(res.data ? res.data.cantidadPedidos : 0);
    })();
  }, [telefonoCliente]);

  /**
   * Sprint 137 — Con cuánto paga, en centavos, o `undefined` si no lo dijo.
   *
   * Un texto que no es un número se trata como "no lo dijo" y no como un error: el dato es
   * opcional y frenar la captura por él sería bloquear una venta por un campo de ayuda.
   */
  const pagaConCentavos = (): number | undefined => {
    if (!pagaAlEntregar) return undefined;
    const n = Number(pagaCon.replace(',', '.').trim());
    if (!Number.isFinite(n) || n <= 0) return undefined;
    return pesosACentavos(n);
  };

  const datos = (): CostoEnvioInfo => ({
    costoEnvio: costoEnvio > 0 ? costoEnvio : 0,
    // Una cortesía no la paga el cliente: las dos banderas juntas serían una contradicción.
    envioPagaCliente: envioCortesia ? false : envioPagaCliente,
    envioCortesia,
    nombreRepartidor: nombreRepartidor.trim() || undefined,
    pagaAlEntregar,
    pagaCon: pagaConCentavos(),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (costoEnvio < 0) {
      setError('El costo de envío no puede ser negativo');
      return;
    }
    onSiguiente(datos());
  };

  /**
   * El mensaje que se copia al grupo de motomandados.
   *
   * Lo arma `shared/mensajesWhatsapp.ts` y **no lleva nombre, teléfono ni la calle exacta**: para
   * cotizar basta el punto. La zona que sí viaja es la última parte de la dirección (la colonia),
   * que es como se pide un precio en el grupo.
   */
  const copiarParaElGrupo = async () => {
    const zona = zonaDeLaDireccion(entrega?.direccion);
    const texto = mensajeCotizacionAlGrupo({
      folio: folio ?? 0,
      clienteLatitud: entrega?.latitud,
      clienteLongitud: entrega?.longitud,
      zona,
      minutosListo: 20,
      nombreNegocio,
    });
    const ok = await copiarAlPortapapeles(texto);
    setCopiado(ok);
    if (!ok) setError('No se pudo copiar. Selecciona el texto a mano.');
  };

  const totalConEnvio =
    envioPagaCliente || envioCortesia
      ? totalPedido
      : totalPedido + (costoEnvio > 0 ? costoEnvio : 0);

  return (
    <Modal
      abierto
      titulo="¿Cuánto cuesta llevarlo?"
      subtitulo={`${etiquetaPaso('envio')} · Envío`}
      ancho="md"
      onSubmit={onSubmit}
      /*
        Sprint 98 — Sin X en este paso.

        Es el único de los tres que puede quedarse esperando a un tercero (la cotización del grupo
        de motomandados), y para eso ya existe una salida con nombre —"el grupo no contesta"— que
        deja el pedido en el tablero en vez de tirarlo. Una X al lado de esa salida invita a cerrar
        y perder la captura justo cuando la espera se hace larga.
      */
      pie={
        <div className="space-y-2">
          {/*
            Sprint 140 — Cómo va a pagar, **siempre a la vista**.

            La pregunta vive dentro de la ventana y, con el mensaje al grupo y la cotización
            encima, queda abajo del borde: al recorrerla en el navegador hubo que bajar para
            encontrarla. Una opción que no se ve no existe — la dueña seguiría pidiendo la
            transferencia por costumbre sin saber que puede mandarlo a cocina.

            El pie sí se ve siempre. Aquí se dice en una línea qué se va a hacer y se ofrece el
            atajo para cambiarlo, que lleva a la pregunta y la resalta.
          */}
          <button
            type="button"
            onClick={() => {
              const destino = document.getElementById('cuandoPagaPedido');
              destino?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }}
            className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm ${
              pagaAlEntregar
                ? 'border-amber-300 bg-amber-50 text-amber-900'
                : 'border-slate-200 bg-slate-50 text-slate-700'
            }`}
          >
            <span>
              {pagaAlEntregar ? (
                <>
                  <b>Se cocina ahora</b> y se cobra al entregar
                </>
              ) : (
                <>
                  <b>Transfiere antes</b> de que se cocine
                </>
              )}
            </span>
            <span className="text-primary underline">Cambiar</span>
          </button>

          {/*
            Sprint 137 — El botón dice lo que va a pasar.

            Con "paga al recibir" el efecto es otro —el pedido se va a la parrilla y no habrá
            pantalla de cobro— y un botón que hace algo distinto según un radio de más arriba,
            sin decirlo, es como se cocinan pedidos por error.
          */}
          <button
            type="submit"
            className={`min-h-[44px] w-full ${
              pagaAlEntregar
                ? 'btn-primary bg-amber-600 hover:bg-amber-700'
                : 'btn-primary'
            }`}
            disabled={enviando}
          >
            {enviando
              ? 'Guardando…'
              : pagaAlEntregar
                ? 'Mandarlo a cocina · cobrar al entregar'
                : 'Continuar a cobro'}
            <ArrowRight className="h-4 w-4" />
          </button>

          {/*
            Sprint 88 — Aquí ya no va nada: "el grupo no contesta" subió al paso 1, que es donde
            nace la espera. Duplicarlo sería peor que esconderlo.
          */}
          <button type="button" onClick={onAtras} className="btn-ghost w-full" disabled={enviando}>
            <ArrowLeft className="h-4 w-4" />
            Atrás
          </button>
        </div>
      }
    >
      {/* A dónde va. Se muestra para no obligar a regresar dos pasos a verificarlo. */}
        {entrega?.direccion && (
          <div className="card flex items-start gap-2 bg-slate-50 py-3 text-sm">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
            <div className="min-w-0">
              <div className="text-slate-700">{entrega.direccion}</div>
              {entrega.referenciasEntrega && (
                <div className="text-xs text-slate-500">{entrega.referenciasEntrega}</div>
              )}
            </div>
          </div>
        )}

        {/*
          Sprint 76 — Pedir la cotización sin salir del sistema ni teclear el mensaje.

          Sprint 88 — El paso decía "Pídeles el precio" y el botón prometía otra cosa. Reporte del
          dueño: *"¿qué te imaginas que piensa el dueño en la sección '1 · Pídeles el precio' y un
          botón que promete una acción?"*. Tenía razón: el botón **no les pide nada**, copia un
          texto que después hay que pegar a mano en WhatsApp. Ahora el título dice el trabajo
          completo y el botón dice **sólo lo que hace**, en una línea.
        */}
        <div className="card space-y-2">
          <div className="text-sm font-medium text-slate-700">
            1 · Copia el mensaje y pégalo en tu grupo
          </div>
          <button
            type="button"
            onClick={copiarParaElGrupo}
            className="btn-ghost min-h-[44px] w-full justify-center border border-slate-300"
          >
            <Copy className="h-4 w-4" />
            {copiado ? '✓ Copiado' : 'Copiar mensaje'}
          </button>
          <p className="text-xs text-slate-500">
            {copiado
              ? 'Pégalo en el grupo de motomandados y espera su respuesta.'
              : 'Lleva la ubicación y el folio. No lleva el nombre ni el teléfono del cliente.'}
          </p>

          {/*
            Sprint 88 — La salida vive **aquí**, donde nace la espera.

            Estaba hasta el fondo de la pantalla, blanca y del mismo tamaño que "Atrás": *"me doy
            cuenta 10 minutos después de un botón blanco, sin color de MÍRAME, y lejos del punto
            focal de donde se solicitó una acción"*. Y no decía a quién se estaba esperando.

            Se pone justo debajo del mensaje que se acaba de mandar, con color, y nombrando al
            sujeto: el que tarda es **el grupo**, no el cliente.
          */}
          {onDejarEsperando && (
            <button
              type="button"
              onClick={() => onDejarEsperando(datos())}
              className="min-h-[44px] w-full justify-center rounded-lg border border-amber-300 bg-amber-50 px-3 text-sm font-medium text-amber-900 hover:bg-amber-100 inline-flex items-center gap-2"
              disabled={enviando}
            >
              <Clock className="h-4 w-4" />
              El grupo no contesta · Guardar y atender a otro cliente
            </button>
          )}
        </div>

        <div className="card space-y-3">
          <div className="text-sm font-medium text-slate-700">2 · Lo que contestaron</div>
          <div>
            <label className="label" htmlFor="cliEnvio">
              Cuota de envío
            </label>
            {/* Sprint 39 — state en centavos, UX en pesos. */}
            <NumberField
              id="cliEnvio"
              min={0}
              decimals={2}
              className="input"
              value={centavosAPesos(costoEnvio)}
              onChange={(n) => setCostoEnvio(pesosACentavos(n))}
              placeholder="0"
            />
          </div>

          {/*
            Sprint 77 — Los atajos de tarifa. Botones, no un valor precargado: la regla del
            negocio es "siempre prefiero cotizar", y un número que aparece solo no se cuestiona.
          */}
          {sugerencia && (sugerencia.zonas.length > 0 || sugerencia.aprendidas.length > 0 || sugerencia.porDistancia) && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-blue-900">
                Lo que se ha cobrado por aquí
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {/*
                  Sprint 111 — La tarifa que le toca por distancia, primera y destacada.

                  Va delante de las otras dos porque es la única que sabe **de este cliente en
                  particular**: las zonas son una lista fija y el histórico es de "por aquí". Sigue
                  siendo un botón y no un valor precargado — la regla del negocio no cambió:
                  *"siempre prefiero cotizar"*, y quien pone el precio real es el motomandado.
                */}
                {sugerencia.porDistancia && (
                  <button
                    key="por-distancia"
                    type="button"
                    onClick={() => setCostoEnvio(sugerencia.porDistancia!.zona.tarifa)}
                    className="min-h-[38px] rounded-lg border-2 border-emerald-400 bg-emerald-50 px-3 text-sm font-semibold text-emerald-900 hover:bg-emerald-100"
                  >
                    {formatPrecio(sugerencia.porDistancia.zona.tarifa, monedaSimbolo)}
                    <span className="ml-1 text-xs font-normal text-emerald-800">
                      · {sugerencia.porDistancia.zona.nombre} ·{' '}
                      {textoDistancia(sugerencia.porDistancia.distanciaKm)}
                    </span>
                  </button>
                )}
                {sugerencia.aprendidas.slice(0, 3).map((a) => (
                  <button
                    key={`hist-${a.tarifa}`}
                    type="button"
                    onClick={() => setCostoEnvio(a.tarifa)}
                    className="min-h-[38px] rounded-lg border border-blue-300 bg-white px-3 text-sm font-medium text-blue-900 hover:bg-blue-100"
                  >
                    {formatPrecio(a.tarifa, monedaSimbolo)}
                    <span className="ml-1 text-xs font-normal text-blue-700">
                      · {explicarTarifa(a)}
                    </span>
                  </button>
                ))}
                {sugerencia.zonas.map((z) => (
                  <button
                    key={`zona-${z.id}`}
                    type="button"
                    onClick={() => setCostoEnvio(z.tarifa)}
                    className="min-h-[38px] rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-100"
                  >
                    {z.nombre}: {formatPrecio(z.tarifa, monedaSimbolo)}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-xs text-blue-800">
                Es una referencia. Confírmala en el grupo antes de dársela al cliente.
              </p>
            </div>
          )}

          {/*
            Sprint 76 — Quién viene por él.

          {/*
            Sprint 140 — **Cuándo paga va ANTES que el resto.**

            Nació al final de esta ventana y ahí quedaba **debajo del pliegue**: al
            recorrerla en el navegador hubo que hacer scroll para encontrarla, y una
            opción que no se ve es una opción que no existe — la dueña seguiría pidiendo
            la transferencia por costumbre.

            Y es la pregunta que más pesa: define **si la comida se cocina antes de
            cobrar**. Quién viene por él y cómo se paga el envío se pueden contestar
            después; esto no.
          */}
          {/*
            Sprint 137 — Cuándo paga el pedido.

            Va debajo del envío y no en un paso nuevo: aquí ya se sabe el total y es la
            conversación que quien atiende está teniendo con el cliente justo en ese momento.
            Después de este paso el pedido se va a la parrilla.

            Lo que NO se pregunta aquí es tan importante como lo que sí: ni quién va a cobrar ni
            con qué método. Eso se sabe al final —el repartidor puede cobrar y traer el efectivo,
            o pagar él y arreglarse con el cliente, o el cliente puede transferir al recibir— y
            preguntarlo ahora sería pedirle a quien captura que adivine.
          */}
          <fieldset id="cuandoPagaPedido" className="space-y-2 border-t border-slate-200 pt-4">
            <legend className="mb-1 text-xs font-medium text-slate-600">
              ¿Cuándo paga el pedido?
            </legend>
            <label className="flex cursor-pointer items-start gap-2 rounded p-2 hover:bg-slate-50">
              <input
                type="radio"
                name="cuandoPaga"
                checked={!pagaAlEntregar}
                onChange={() => setPagaAlEntregar(false)}
                className="mt-0.5"
              />
              <div className="text-sm">
                <div className="font-medium">Transfiere ahora, antes de cocinarlo</div>
                <div className="text-xs text-slate-500">
                  Como siempre. No entra a la parrilla hasta ver el pago.
                </div>
              </div>
            </label>
            <label className="flex cursor-pointer items-start gap-2 rounded p-2 hover:bg-slate-50">
              <input
                type="radio"
                name="cuandoPaga"
                checked={pagaAlEntregar}
                onChange={() => setPagaAlEntregar(true)}
                className="mt-0.5"
              />
              <div className="text-sm">
                <div className="font-medium">Paga al recibir</div>
                <div className="text-xs text-slate-500">
                  Va a cocina ahora y se cobra cuando se entregue. Queda como dinero por cobrar.
                </div>
                {/*
                  El historial, pegado a la opción que lo necesita. Sólo se afirma cuando se
                  sabe: mientras la consulta no vuelve no se dice nada.
                */}
                {pedidosPrevios !== null && (
                  <div
                    className={`mt-1.5 inline-block rounded-md border px-2 py-1 text-xs ${
                      pedidosPrevios > 0
                        ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                        : 'border-amber-300 bg-amber-50 text-amber-800'
                    }`}
                  >
                    {pedidosPrevios > 0
                      ? `✓ Ya te ha pedido ${pedidosPrevios} ${pedidosPrevios === 1 ? 'vez' : 'veces'}`
                      : 'Es la primera vez que te pide'}
                  </div>
                )}
              </div>
            </label>

            {pagaAlEntregar && (
              <div className="space-y-3 rounded-lg border border-amber-300 bg-amber-50 p-3">
                <p className="text-xs text-amber-900">
                  <b>Este pedido se cocina sin haber cobrado.</b> Si el cliente no está o no
                  paga, la comida ya se hizo. Queda registrado que tú lo autorizaste.
                </p>
                <div>
                  <label className="label" htmlFor="pagaCon">
                    ¿Con cuánto va a pagar?{' '}
                    <span className="font-normal text-slate-600">(opcional)</span>
                  </label>
                  <input
                    id="pagaCon"
                    className="input"
                    inputMode="decimal"
                    value={pagaCon}
                    onChange={(e) => setPagaCon(e.target.value)}
                    placeholder="500"
                    maxLength={10}
                  />
                  <p className="mt-1 text-xs text-slate-600">
                    Para que la moto salga con el cambio. Si no lo dijo, déjalo vacío.
                  </p>
                </div>
              </div>
            )}
          </fieldset>

          {/*
            Quién viene por el pedido.

            Se sabe aquí, no en Despacho. Capturarlo en este momento convierte el despacho en un
            clic de confirmación en lugar de un tecleo con el repartidor esperando.
          */}
          <div>
            <label className="label" htmlFor="cliRepartidor">
              ¿Quién viene por él?
            </label>
            <div className="flex items-center gap-2">
              <Bike className="h-4 w-4 shrink-0 text-slate-500" />
              <input
                id="cliRepartidor"
                className="input"
                value={nombreRepartidor}
                onChange={(e) => setNombreRepartidor(e.target.value)}
                placeholder="Juan, El güero, yo misma…"
                maxLength={80}
              />
            </div>
            <p className="mt-1 text-xs text-slate-500">
              Opcional. Si lo pones ahora, al despachar sólo lo confirmas.
            </p>
          </div>

          {/*
            Sprint 98 — Las tres formas de pagar el envío, en un solo grupo y en el orden del piso.

            Eran una casilla de "cortesía" **encima** de dos opciones que apagaba en gris: tres
            posibilidades excluyentes contadas con dos controles distintos, y con la menos frecuente
            arriba de todo. Reporte del dueño del producto, que además fijó el orden:
            *"incluirlo en el monto a pagar | pago contra entrega | cortesía del negocio"*.

            Es el orden por frecuencia real del negocio: a domicilio se cobra por adelantado, así
            que lo normal es que el envío vaya dentro del total; contra-entrega es la excepción y la
            cortesía es la excepción de la excepción. Lo más usado, primero y sin leer nada más.
          */}
          <fieldset className="space-y-2">
            <legend className="mb-1 text-xs font-medium text-slate-600">
              ¿Cómo se paga el envío?
            </legend>
            <label className="flex cursor-pointer items-start gap-2 rounded p-2 hover:bg-slate-50">
              <input
                type="radio"
                name="formaPagoEnvio"
                checked={!envioCortesia && !envioPagaCliente}
                onChange={() => {
                  setEnvioCortesia(false);
                  setEnvioPagaCliente(false);
                }}
                className="mt-0.5"
              />
              <div className="text-sm">
                <div className="font-medium">Incluirlo en el monto a pagar</div>
                <div className="text-xs text-slate-500">
                  El cliente paga todo junto al pedir (alimentos + envío).
                </div>
              </div>
            </label>
            <label className="flex cursor-pointer items-start gap-2 rounded p-2 hover:bg-slate-50">
              <input
                type="radio"
                name="formaPagoEnvio"
                checked={!envioCortesia && envioPagaCliente}
                onChange={() => {
                  setEnvioCortesia(false);
                  setEnvioPagaCliente(true);
                }}
                className="mt-0.5"
              />
              <div className="text-sm">
                <div className="font-medium">Pago contra entrega</div>
                <div className="text-xs text-slate-500">
                  El repartidor cobra el envío aparte. No se incluye en el cobro inicial.
                </div>
              </div>
            </label>
            {/* Sprint 77 — El tercer caso: lo absorbe el negocio. */}
            <label className="flex cursor-pointer items-start gap-2 rounded p-2 hover:bg-slate-50">
              <input
                type="radio"
                name="formaPagoEnvio"
                checked={envioCortesia}
                onChange={() => setEnvioCortesia(true)}
                className="mt-0.5"
              />
              <div className="text-sm">
                <div className="font-medium">Cortesía del negocio</div>
                <div className="text-xs text-slate-500">
                  El cliente no lo paga. Se registra el costo para saber cuánto suma al mes.
                </div>
              </div>
            </label>
          </fieldset>

        </div>

        {/* Sprint 39 — Todos los montos en centavos; formatPrecio divide /100. */}
        <div className="card space-y-1 bg-slate-50">
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">Productos</span>
            <span className="font-medium">{formatPrecio(totalPedido, monedaSimbolo)}</span>
          </div>
          {!envioPagaCliente && costoEnvio > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Envío incluido</span>
              <span className="font-medium">{formatPrecio(costoEnvio, monedaSimbolo)}</span>
            </div>
          )}
          <div className="mt-1 flex justify-between border-t border-slate-200 pt-2 text-base">
            {/*
              Sprint 137 — El renglón dice **cuándo** entra el dinero, no sólo cuánto.
              "Total a cobrar" sobre un pedido que sale sin pagarse se lee como si ya estuviera
              cobrado, y es justo lo contrario.
            */}
            <span className="font-semibold">
              {pagaAlEntregar ? 'Se cobra al entregar' : 'Total a cobrar'}
            </span>
            <span className={`font-bold ${pagaAlEntregar ? 'text-amber-700' : 'text-primary'}`}>
              {formatPrecio(totalConEnvio, monedaSimbolo)}
            </span>
          </div>
        </div>

        {error && (
          <div className="error-text" role="alert">
            {error}
          </div>
        )}
    </Modal>
  );
}

/**
 * La zona que se le dice al grupo: la última parte de la dirección (colonia o fraccionamiento).
 *
 * Se manda **sólo** eso, nunca la calle y el número: para cotizar un envío basta saber la
 * colonia, y la dirección exacta es dato del cliente. Si la dirección viene de un enlace de
 * mapa o de coordenadas, no se manda ninguna zona — el punto ya va en el enlace.
 */
export function zonaDeLaDireccion(direccion?: string | null): string | undefined {
  const texto = (direccion ?? '').trim();
  if (!texto) return undefined;
  if (/^https?:\/\//i.test(texto)) return undefined;
  if (/^Ubicación GPS/i.test(texto)) return undefined;
  const partes = texto
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  if (partes.length < 2) return undefined;
  const zona = partes[partes.length - 1];
  // Un número suelto al final no es una colonia: es parte de la calle.
  if (/^\d+$/.test(zona)) return undefined;
  return zona.slice(0, 60);
}
